import { useEffect, useRef } from "react";

/**
 * Subtle golden blob that follows the mouse within its parent.
 * Mount inside a `position: relative` container.
 */
export function CursorFollower({ size = 380 }: { size?: number }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const parent = el.parentElement;
    if (!parent) return;
    // Skippa på touch-enheter — ingen muspekare att följa.
    if (typeof window !== "undefined" && window.matchMedia("(hover: none), (pointer: coarse)").matches) return;

    let raf = 0;
    let x = 0, y = 0, tx = 0, ty = 0;

    const onMove = (e: MouseEvent) => {
      const r = parent.getBoundingClientRect();
      tx = e.clientX - r.left;
      ty = e.clientY - r.top;
      el.style.opacity = "1";
    };
    const onLeave = () => { el.style.opacity = "0"; };

    const tick = () => {
      x += (tx - x) * 0.12;
      y += (ty - y) * 0.12;
      el.style.transform = `translate3d(${x - size / 2}px, ${y - size / 2}px, 0)`;
      raf = requestAnimationFrame(tick);
    };

    parent.addEventListener("mousemove", onMove);
    parent.addEventListener("mouseleave", onLeave);
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      parent.removeEventListener("mousemove", onMove);
      parent.removeEventListener("mouseleave", onLeave);
    };
  }, [size]);

  return (
    <div
      ref={ref}
      aria-hidden
      className="pointer-events-none absolute top-0 left-0 z-[1] rounded-full opacity-0 transition-opacity duration-500 mix-blend-screen"
      style={{
        width: size,
        height: size,
        background:
          "radial-gradient(circle at center, rgba(230,200,112,0.35) 0%, rgba(196,151,58,0.18) 35%, transparent 70%)",
        filter: "blur(20px)",
      }}
    />
  );
}
